import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    ContentLayout,
    Header,
    SpaceBetween,
    Button,
    Box,
    Icon,
    Link
} from '@amzn/awsui-components-console';
import { getErrorIllustration } from '../atoms/ErrorIllustrations';
import './ErrorPage.scss';

interface ErrorPageProps {
    errorCode?: '404' | '403' | '500' | 'generic';
    errorTitle?: string;
    errorMessage?: string;
    showHomeButton?: boolean;
    showBackButton?: boolean;
    customAction?: {
        label: string;
        onClick: () => void;
    };
}

const ErrorPage: React.FC<ErrorPageProps> = ({
    errorCode = 'generic',
    errorTitle,
    errorMessage,
    showHomeButton = true,
    showBackButton = true,
    customAction
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get error details from location state if available
    const locationState = location.state as { 
        errorTitle?: string; 
        errorMessage?: string;
        errorCode?: string;
    } | null;
    
    // Use props or fallback to location state or defaults
    const finalErrorCode = errorCode || locationState?.errorCode || 'generic';
    const finalErrorTitle = errorTitle || locationState?.errorTitle || getDefaultTitle(finalErrorCode);
    const finalErrorMessage = errorMessage || locationState?.errorMessage || getDefaultMessage(finalErrorCode);
    
    function getDefaultTitle(code: string): string {
        switch (code) {
            case '404':
                return 'Page not found';
            case '403':
                return 'Access denied';
            case '500':
                return 'Internal server error';
            default:
                return 'Something went wrong';
        }
    }
    
    function getDefaultMessage(code: string): string {
        switch (code) {
            case '404':
                return "The page you're looking for doesn't exist or has been moved.";
            case '403':
                return "You don't have permission to access this resource.";
            case '500':
                return 'An unexpected error occurred. Please try again later.';
            default:
                return 'An error occurred while processing your request.';
        }
    }
    
    function getErrorIcon(code: string) {
        switch (code) {
            case '404':
                return 'search';
            case '403':
                return 'lock-private';
            case '500':
                return 'status-warning';
            default:
                return 'status-warning';
        }
    }
    
    const handleGoHome = () => {
        navigate('/');
    };
    
    const handleGoBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };
    
    return (
        <div className="error-page">
            {/* Floating orbs for visual interest */}
            <div className="floating-orb"></div>
            <div className="floating-orb"></div>
            <div className="floating-orb"></div>
            
            <ContentLayout
                headerVariant="high-contrast"
                defaultPadding={false}
            >
                <Container>
                    <Box textAlign="center" padding={{ vertical: "xxl" }}>
                        <SpaceBetween size="xl" alignItems="center">
                            {/* Error Illustration */}
                            <div className="error-illustration">
                                {getErrorIllustration(finalErrorCode)}
                            </div>
                            
                            {/* Error Title */}
                            <Box fontSize="heading-xl" fontWeight="bold" className="error-title">
                                {finalErrorTitle}
                            </Box>
                            
                            {/* Error Message */}
                            <Box fontSize="body-m" color="text-body-secondary" margin={{ horizontal: "xxl" }} className="error-message">
                                {finalErrorMessage}
                            </Box>
                        
                        {/* Attempted URL for 404 */}
                        {finalErrorCode === '404' && location.pathname !== '/error' && (
                            <Box fontSize="body-s" color="text-status-inactive">
                                <code>{location.pathname}</code>
                            </Box>
                        )}
                        
                        {/* Action Buttons */}
                        <SpaceBetween direction="horizontal" size="s">
                            {showBackButton && (
                                <Button 
                                    onClick={handleGoBack}
                                    variant="normal"
                                    iconName="arrow-left"
                                >
                                    Go Back
                                </Button>
                            )}
                            {showHomeButton && (
                                <Button 
                                    onClick={handleGoHome}
                                    variant="primary"
                                    iconName="external"
                                >
                                    Go to Home
                                </Button>
                            )}
                            {customAction && (
                                <Button 
                                    onClick={customAction.onClick}
                                    variant="primary"
                                >
                                    {customAction.label}
                                </Button>
                            )}
                        </SpaceBetween>
                    
                        </SpaceBetween>
                    </Box>
                </Container>
            </ContentLayout>
        </div>
    );
};

export default ErrorPage;
